import { useState, useCallback, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAdminData } from "../../../store/adminDataStore";
import { getSupabaseClient } from "../../../lib/supabaseClient";
import { CITY_CONFIG } from "../../../config/cities";
import { MOCK_ADDONS } from "../../../mocks/MOCK_services";
import LegalDocsAcceptance from "./LegalDocsAcceptance";

interface BookingData {
  city: string;
  service: string;
  date: string;
  addons: string[];
  photographer_preference?: "none" | "female_preferred";
}

interface CheckoutStepProps {
  bookingData: BookingData;
  onBack: () => void;
  onConfirm: () => void;
}

export default function CheckoutStep({ bookingData, onBack, onConfirm }: CheckoutStepProps) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.split("-")[0] ?? "en") as "zh" | "en" | "ja";
  const { state } = useAdminData();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [requests, setRequests] = useState("");
  const [photographerPref, setPhotographerPref] = useState<"none" | "female_preferred">(
    bookingData.photographer_preference ?? "none"
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Legal doc acceptance state
  const [acceptedDocIds, setAcceptedDocIds] = useState<string[]>([]);
  const [allMandatoryAccepted, setAllMandatoryAccepted] = useState(true); // true by default (in case no docs configured)
  const [legalError, setLegalError] = useState(false);

  const cityConfig = CITY_CONFIG.find((c) => c.id === bookingData.city);
  const serviceData = state.services.find(
    (s) => s.service_code === bookingData.service || s.id === bookingData.service
  );
  const addonData = MOCK_ADDONS.filter((a) => bookingData.addons.includes(a.id));
  const cityName = cityConfig ? cityConfig.name[lang] : bookingData.city;
  const serviceName = serviceData ? serviceData[`title_${lang}`] : bookingData.service;

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = t("tabifoto.booking.field_required", "必填");
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) errs.email = t("tabifoto.booking.field_email_invalid", "請輸入有效的電子郵件");
    if (!phone.trim()) errs.phone = t("tabifoto.booking.field_required", "必填");
    return errs;
  };

  const handleLegalChange = useCallback((ids: string[], allAccepted: boolean) => {
    setAcceptedDocIds(ids);
    setAllMandatoryAccepted(allAccepted);
    if (allAccepted) setLegalError(false);
  }, []);

  const submitLegalAcceptances = async (acceptorEmail: string) => {
    if (acceptedDocIds.length === 0) return;
    try {
      const supabase = getSupabaseClient();
      const records = acceptedDocIds.map((docId) => ({
        actor_type: "customer",
        actor_id: acceptorEmail,
        document_id: docId,
        accepted_at: new Date().toISOString(),
        user_agent: navigator.userAgent,
      }));
      await supabase.from("legal_acceptance_logs").insert(records);
    } catch {
      // Non-blocking — don't fail the booking over acceptance log insert failure
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    if (!allMandatoryAccepted) {
      setLegalError(true);
      // Scroll to legal section
      document.getElementById("legal-docs-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 800));
      await submitLegalAcceptances(email);
      onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-extralight text-[#2C2C2C] mb-3" style={{ letterSpacing: "-0.02em" }}>
          {t("tabifoto.booking.checkout_title")}
        </h2>
        <div className="w-8 h-px bg-[#C8B8A6] mx-auto" />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form fields — 3 cols */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                {t("tabifoto.booking.your_name")}
              </label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] transition-colors" />
              {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                {t("tabifoto.booking.your_email")}
              </label>
              <input type="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] transition-colors" />
              {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                {t("tabifoto.booking.your_phone")}
              </label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] transition-colors" />
              {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-2">
                {t("tabifoto.booking.special_requests")}
              </label>
              <textarea value={requests} onChange={(e) => { if (e.target.value.length <= 500) setRequests(e.target.value); }}
                rows={3} maxLength={500}
                className="w-full bg-transparent border-b border-[#D9D9D9] pb-2 text-sm text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] transition-colors resize-none" />
              <p className="text-xs text-[#2C2C2C]/30 mt-1 text-right">{requests.length}/500</p>
            </div>

            {/* ── Photographer preference ──────────────────────────────────── */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-3">
                {t("tabifoto.booking.photographer_preference_label")}
              </label>
              <div className="flex gap-3">
                {(["none", "female_preferred"] as const).map((pref) => (
                  <button
                    key={pref}
                    type="button"
                    onClick={() => setPhotographerPref(pref)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-xs font-light transition-all duration-150 cursor-pointer whitespace-nowrap ${
                      photographerPref === pref
                        ? "border-[#C8B8A6] bg-[#C8B8A6]/10 text-[#2C2C2C]"
                        : "border-[#D9D9D9] text-[#2C2C2C]/50 hover:border-[#C8B8A6]/50"
                    }`}
                  >
                    <div className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 ${
                      photographerPref === pref ? "border-[#C8B8A6] bg-[#C8B8A6]" : "border-[#D9D9D9]"
                    }`}>
                      {photographerPref === pref && (
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </div>
                    {t(`tabifoto.booking.photographer_preference_${pref}`)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#2C2C2C]/30 mt-2 leading-relaxed">
                {t("tabifoto.booking.photographer_preference_note")}
              </p>
            </div>

            {/* ── Legal acceptance ─────────────────────────────────────── */}
            <div id="legal-docs-section">
              <LegalDocsAcceptance onChange={handleLegalChange} />
              {legalError && (
                <div className="flex items-center gap-2 mt-3 px-3 py-2.5 bg-rose-50 rounded-lg border border-rose-100">
                  <div className="w-4 h-4 flex items-center justify-center text-rose-500 shrink-0">
                    <i className="ri-error-warning-line text-sm" />
                  </div>
                  <p className="text-xs font-light text-rose-700">
                    {t("tabifoto.booking.legal_must_accept", "請勾選所有必填條款後才能完成預訂")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Order summary — 2 cols */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#D9D9D9] rounded-lg p-6 sticky top-24">
              <h3 className="text-xs uppercase tracking-widest text-[#2C2C2C]/50 mb-5">
                {t("tabifoto.booking.order_summary")}
              </h3>
              <div className="space-y-3 text-sm">
                {[
                  { label: t("tabifoto.booking.city_label"), value: cityName || "—" },
                  { label: t("tabifoto.booking.service_label"), value: serviceName || "—" },
                  { label: t("tabifoto.booking.date_label"), value: bookingData.date || "—" },
                  {
                    label: t("tabifoto.booking.addons_label"),
                    value: addonData.length
                      ? addonData.map((a) => t(a.title_key)).join(", ")
                      : t("tabifoto.booking.none"),
                  },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div className="flex items-start justify-between gap-4">
                      <span className="text-[#2C2C2C]/40 shrink-0">{label}</span>
                      <span className="text-[#2C2C2C] text-right font-light">{value}</span>
                    </div>
                    <div className="h-px bg-[#F7F6F3] mt-3" />
                  </div>
                ))}
              </div>
              {serviceData?.base_price_twd && serviceData.base_price_twd > 0 && (
                <div className="mt-3 flex justify-between text-sm">
                  <span className="text-[#2C2C2C]/40">From</span>
                  <span className="text-[#2C2C2C] font-light">NT${serviceData.base_price_twd.toLocaleString()}</span>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-[#D9D9D9]">
                <p className="text-xs text-[#2C2C2C]/30 text-center leading-relaxed">
                  Our team will confirm pricing and photographer within 24 hours.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-10">
          <button type="button" onClick={onBack}
            className="px-6 py-3 rounded-full border border-[#D9D9D9] text-sm tracking-widest uppercase text-[#2C2C2C]/50 hover:border-[#2C2C2C] hover:text-[#2C2C2C] transition-all duration-200 whitespace-nowrap cursor-pointer">
            ← {t("tabifoto.booking.back")}
          </button>
          <button type="submit" disabled={loading}
            className="px-10 py-3 rounded-full bg-[#2C2C2C] text-white text-sm tracking-widest uppercase hover:bg-[#2C2C2C]/80 transition-all duration-200 disabled:opacity-50 whitespace-nowrap cursor-pointer">
            {loading ? "..." : t("tabifoto.booking.confirm_booking")}
          </button>
        </div>
      </form>
    </div>
  );
}